import { useEffect, useRef, useState } from 'react';
import { Button, Empty, Select, Spin } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import {
  getJobPredictionDataVersions,
  type JobPredictionDataVersionOption,
} from '@apis/simulation';

interface Props {
  id?: string;
  value?: string;
  onChange?: (value?: string) => void;
  /** 从版本号解析出的预测数据版本号（dataVersion，如 AD3.1.3）；latest 默认值按它匹配当前版本。 */
  dataVersion?: string | null;
  /**
   * 系统参数配置的默认模式（``default`` / ``latest``）：
   * - ``default`` → 自动填入 ``default``；
   * - ``latest``  → 自动填入该 dataVersion **当前生效版本对应的完整预测数据版本串**
   *   （``prediction.AD3.1.3``），当前还没有生效版本时退回 ``latest``
   *   （后端同样按该 job 的 dataVersion 解析 current，解析不到回落 default）。
   */
  defaultMode?: string;
  /** 父弹窗 reset / 复制回填完成的批次号：变化时重跑一次默认值补齐。 */
  formEpoch?: number;
}

/**
 * 模式项与具体版本号同列，值域与后端 job.external['prediction_data_version'] 一致。
 * 下拉不再展示 latest；default 只显示中文「record 包内原始数据」（提交值仍为 ``default``）。
 */
const DEFAULT_LABEL = 'record 包内原始数据';
const MODE_OPTIONS = [
  {
    value: 'default',
    label: DEFAULT_LABEL,
    title: '使用 record 包内自带的原始预测，不做替换',
  },
];

/** 默认模式 → 实际要填入的值；latest 取全平台唯一的当前版本，没有则 default。 */
function resolveDefaultValue(
  mode: string,
  options: JobPredictionDataVersionOption[],
): string {
  if (mode !== 'latest') return 'default';
  // 预测数据不再与 job 版本对应：当前版本全平台唯一，直接取它。
  const current = options.find(option => option.isCurrent);
  // 系统默认 latest：有当前生效版本直接用它，没有则用 record 包内原始数据。
  return current?.predictionVersion || 'default';
}

/**
 * LPL 任务的「预测数据版本」选择器（交互参照模型主版本 ModelReleasePicker）。
 *
 * 候选**不再按 tag 拉全部内部版本**，而是来自「预测数据版本列表」中打开了
 * 「创建任务展示」开关的**完整预测数据版本串**（``GET /job/prediction-data-versions``）：
 * - 前两项是模式项 latest / default；
 * - 其余是目录里勾选了「创建任务展示」的 predictionVersion（如 ``prediction.AD3.1.3``），
 *   标注「当前」与描述；
 * - 打开时按系统参数默认模式预填（default → ``default``；latest → 该 job dataVersion 下
 *   当前生效版本的完整串，没有则填 ``latest`` 交后端解析）；
 *   默认 latest 且解析不出 dataVersion 时兜底 `orin_dev`；
 * - 复制回填 / 用户手选后不再覆盖；显式选择的值会原样提交，后端按“当前生效优先、
 *   否则最新”的 header 反查内部版本后再下发，引擎侧契约不变。
 */
export default function PredictionDataVersionPicker({
  id, value, onChange, defaultMode = 'latest', formEpoch = 0,
}: Props) {
  const [options, setOptions] = useState<JobPredictionDataVersionOption[]>([]);
  const [loading, setLoading] = useState(false);
  // 首次候选是否已就绪：就绪前不填默认值，避免先填 latest 再被当前版本覆盖闪一下。
  const [loaded, setLoaded] = useState(false);
  const [revision, setRevision] = useState(0);

  // effect 只依赖候选 / 默认模式 / tag / 刷新计数：用 ref 读最新 value / onChange，
  // 避免选一次值就重跑默认值补齐。
  const valueRef = useRef(value);
  valueRef.current = value;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  // 用户是否手动选过（手选后默认值变化不再覆盖）。
  const manualRef = useRef(false);
  // 最近一次自动填入的值：值仍等于它时，视为“未被用户改过”，可随 tag / 默认模式重填。
  const autoValueRef = useRef<string | undefined>(undefined);

  // 候选 = 「预测数据版本列表」里开启「创建任务展示」的版本号（普通用户可读别名接口，
  // fail-soft：失败回 []，仍可选 record 包内原始数据）。
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getJobPredictionDataVersions()
      .then((list) => {
        if (!cancelled) setOptions(list);
      })
      .finally(() => {
        if (!cancelled) {
          setLoaded(true);
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [revision]);

  useEffect(() => {
    if (!loaded || loading) return;
    const picked = valueRef.current;
    if (manualRef.current && picked) return;
    if (picked && picked !== autoValueRef.current) return;
    const target = resolveDefaultValue(defaultMode, options);
    autoValueRef.current = target;
    if (picked !== target) onChangeRef.current?.(target);
  }, [options, loaded, loading, defaultMode, formEpoch]);

  const handleChange = (next?: string) => {
    manualRef.current = true;
    autoValueRef.current = undefined;
    onChange?.(next);
  };

  const selectOptions = [
    ...MODE_OPTIONS,
    ...options
      // 目录里若人工加过与模式同名的版本号，避免与 latest / default 选项撞 value。
      .filter(
        option =>
          option.predictionVersion !== 'latest' && option.predictionVersion !== 'default',
      )
      .map(option => ({
        value: option.predictionVersion,
        label: `${option.predictionVersion}${option.isCurrent ? '（当前）' : ''}${
          option.description ? ` · ${option.description}` : ''
        }`,
        title: option.description || option.predictionVersion,
      })),
  ];
  // 复制过来的历史值（内部串 / 已撤下展示的版本号）即使不在候选里也保留原值，
  // 避免被清空；后端对内部串仍按老口径接受。
  // latest 不再作为可选项，但历史复制值为 latest 时仍原样保留。
  if (value && !selectOptions.some(option => option.value === value)) {
    selectOptions.push({ value, label: value, title: value });
  }

  return (
    <div className="job-model-release-select">
      <Select
        id={id}
        value={value}
        onChange={handleChange}
        showSearch
        allowClear
        optionFilterProp="label"
        placeholder="record 包内原始数据 / 选择预测数据版本"
        loading={loading}
        options={selectOptions}
        notFoundContent={
          loading
            ? <Spin size="small" />
            : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="暂无开启「创建任务展示」的预测数据版本"
              />
            )
        }
        style={{ flex: 1, minWidth: 0 }}
      />
      <Button
        title="刷新预测数据版本列表"
        aria-label="刷新预测数据版本列表"
        icon={<ReloadOutlined />}
        loading={loading}
        onClick={() => setRevision(current => current + 1)}
      />
    </div>
  );
}